<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useConnection, useTestConnection, useRefreshConnection, useRotateConnection, useDeleteConnection, useUpdateConnection, useStartAuthorization } from "@/queries/connections";
import { useConnectionTemplate } from "@/queries/connection-templates";
import { useAlertStore } from "@/stores/alerts-store";
import { connectionBadgeClass, connectionBadgeLabel, needsReconnect } from "../utils/connectionStatus";
import PanelGrid from "@/views/shared/components/PanelGrid.vue";
import PanelCard from "@/views/shared/components/PanelCard.vue";
import PageHeader from "@/components/PageHeader.vue";
import SkeletonLoader from "@/components/SkeletonLoader.vue";
import ConnectionDiagnosticsTab from "./ConnectionDiagnosticsTab.vue";

const route = useRoute();
const router = useRouter();
const alertStore = useAlertStore();

const connectionId = computed(() => route.params.connectionid as string);
const { connection, connectionLoading: loading, connectionError: error } = useConnection(connectionId);

// Tabs (ProductDetailView pattern): active tab tracked in the route hash so it survives reload and
// back/forward. Overview is the detail panel; Diagnostics is the per-connection event log.
const tabs = [
  { id: "overview", label: "Overview" },
  { id: "diagnostics", label: "Diagnostics" },
];
const validTabs = tabs.map((t) => t.id);
const activeTab = ref(validTabs.includes(route.hash.slice(1)) ? route.hash.slice(1) : "overview");

function setActiveTab(tab: string) {
  activeTab.value = tab;
  router.replace({ ...route, hash: `#${tab}` });
}

watch(() => route.hash, (h) => {
  const tab = h.slice(1);
  if (validTabs.includes(tab)) activeTab.value = tab;
});

const templateId = computed(() => connection.value?.templateId ?? "");
const { template } = useConnectionTemplate(templateId);

const { testConnection, testConnectionPending } = useTestConnection();
const { refreshConnection, refreshConnectionPending } = useRefreshConnection();
const { rotateConnection, rotateConnectionPending } = useRotateConnection();
const { deleteConnection } = useDeleteConnection();
const { startAuthorization, startAuthorizationPending } = useStartAuthorization();

// The connection's chosen method on its template, resolved by id — gives us its kind + label.
const connectionMethod = computed(() =>
  template.value?.authMethods.find((m) => m.id === connection.value?.authMethodId));
const isOauth = computed(() => connectionMethod.value?.kind === "oauth2-authcode");

// Show a reconnect prompt when setup never completed (PendingSetup) or auth broke (RequiresReauth).
// The action depends on the method: oauth re-authorizes; others re-enter creds.
const showReconnect = computed(() =>
  !!connection.value && (connection.value.status === "PendingSetup" || needsReconnect(connection.value)));
const reconnectLabel = computed(() =>
  connection.value?.status === "PendingSetup" ? "Continue setup" : "Reconnect");
const reconnectBusy = computed(() => startAuthorizationPending.value || rotateConnectionPending.value);

function reconnect() {
  if (!connection.value) return;
  if (isOauth.value) {
    startAuthorization(connection.value.id, {
      onSuccess: (r) => { window.location.href = r.authorizeUrl; },
      onError: (e) => alertStore.addError("Authorization failed", e.userMessage),
    });
  } else {
    openRotate();
  }
}

const templateName = computed(() => template.value?.name ?? connection.value?.templateId ?? "");

// Which rate this connection is actually held to: its own override, else what its template declares,
// else the platform ceiling — the same order the engine resolves in (LaneRateReader). With nothing
// declared the number is ours rather than the vendor's, which is what the label says (ADR-035 §4).
const NO_VENDOR_LIMIT = "No vendor limit";

function rateText(rate?: { sends: number; perSeconds: number } | null) {
  return rate ? `${rate.sends} per ${rate.perSeconds === 1 ? "second" : `${rate.perSeconds} seconds`}` : null;
}

const declaredRate = computed(() => rateText(template.value?.rateLimit) ?? NO_VENDOR_LIMIT);
const effectiveRate = computed(() => rateText(connection.value?.rateLimit) ?? declaredRate.value);
const rateSource = computed(() => {
  if (connection.value?.rateLimit) return null;
  return template.value?.rateLimit ? "from the template" : "held to 100 a second, which is ours";
});

const { updateConnection, updateConnectionPending } = useUpdateConnection();
const editingRate = ref(false);
const rateSends = ref<number | string>("");
const ratePerSeconds = ref<number | string>("");

function startEditingRate() {
  rateSends.value = connection.value?.rateLimit?.sends ?? "";
  ratePerSeconds.value = connection.value?.rateLimit?.perSeconds ?? "";
  editingRate.value = true;
}

// A rate is both numbers or none at all, so clearing either field is read as going back to the template's
// rate. Only rateLimit travels: the update is partial, so nothing else on the connection is touched by an
// edit made here.
function saveRate() {
  if (!connection.value) return;
  const sends = Number(rateSends.value);
  const perSeconds = Number(ratePerSeconds.value);
  updateConnection(
    {
      id: connection.value.id,
      data: { rateLimit: sends && perSeconds ? { rate: { sends, perSeconds } } : {} },
    },
    {
      onSuccess: () => { editingRate.value = false; },
      onError: (e) => alertStore.addError("Could not set the rate limit", e.userMessage),
    }
  );
}

// Secret fields of the connection's chosen method — what "rotate" re-enters.
const secretFields = computed(() =>
  connectionMethod.value?.fields.filter((f) => f.input && f.secret) ?? []);

const showRotate = ref(false);
const rotateValues = ref<Record<string, string>>({});

function openRotate() {
  rotateValues.value = {};
  showRotate.value = true;
}

function runTest() {
  if (!connection.value) return;
  testConnection(connection.value.id, {
    onSuccess: (r) => r.success
      ? alertStore.addSuccess("Test passed", "Connected successfully.")
      : alertStore.addError("Test failed", r.message ?? "Could not connect."),
    onError: (e) => alertStore.addError("Test failed", e.userMessage),
  });
}

// Refresh applies to a Connected connection with an expiring token — a token that never expires
// (apikey/basic) has nothing to renew, and a broken one is repaired via the reconnect banner.
const canRefresh = computed(() =>
  connection.value?.status === "Connected" && !!connection.value.expiresAt);

// Proactive re-consent for a still-healthy oauth connection (new scopes, suspected vendor-side
// revocation). Broken connections re-authorize via the banner; non-oauth re-auth is Rotate.
const canReauthorize = computed(() => isOauth.value && connection.value?.status === "Connected");

function runRefresh() {
  if (!connection.value) return;
  refreshConnection(connection.value.id, {
    onSuccess: (r) => r.success
      ? alertStore.addSuccess(r.renewed ? "Token renewed" : "Already current", r.message)
      : alertStore.addError("Refresh failed", r.message),
    onError: (e) => alertStore.addError("Refresh failed", e.userMessage),
  });
}

function confirmRotate() {
  if (!connection.value) return;
  rotateConnection(
    { id: connection.value.id, secrets: { ...rotateValues.value } },
    {
      onSuccess: () => { showRotate.value = false; alertStore.addSuccess("Rotated", "Credentials updated."); },
      onError: (e) => alertStore.addError("Rotate failed", e.userMessage),
    });
}

function remove() {
  if (!connection.value) return;
  if (!confirm(`Delete "${connection.value.name}"? It will be removed; workflows referencing it will fail.`)) return;
  deleteConnection(connection.value.id, {
    onSuccess: () => { alertStore.addSuccess("Deleted", "Connection deleted."); router.push("/connections"); },
    onError: (e) => alertStore.addError("Delete failed", e.userMessage),
  });
}

function goBack() {
  router.back();
}

function fmt(d?: string | null): string {
  return d ? new Date(d).toLocaleString() : "—";
}
</script>

<template>
  <main>
    <PanelGrid :columns="1">
      <PanelCard>
        <template v-if="loading || error">
          <SkeletonLoader type="header" :error="error" />
        </template>

        <template v-else-if="connection">
          <PageHeader
            :title="connection.name"
            :breadcrumbs="[{ label: 'Dashboard', href: '/' }, { label: 'Connections', href: '/connections' }, { label: connection.name }]"
          >
            <template #actions>
              <button class="bb-btn-secondary-cta" @click="goBack">
                <i class="bi bi-arrow-left me-1"></i> Back
              </button>
              <button
                class="bb-btn-secondary-cta"
                :disabled="testConnectionPending || connection.status !== 'Connected'"
                @click="runTest"
              >
                <i class="bi bi-plug me-1"></i> Test
              </button>
              <button class="bb-btn-secondary-cta" @click="router.push(`/connections/${connection.id}/edit`)">
                <i class="bi bi-pencil me-1"></i> Edit
              </button>
              <div class="dropdown">
                <button type="button" class="bb-btn-secondary-cta dropdown-toggle" title="More actions" data-bs-toggle="dropdown">
                  <i class="bi bi-three-dots"></i>
                </button>
                <ul class="dropdown-menu dropdown-menu-end">
                  <li v-if="canRefresh">
                    <a class="dropdown-item" :class="{ disabled: refreshConnectionPending }" href="#" @click.prevent="runRefresh"><i class="bi bi-arrow-clockwise me-2"></i>Refresh token</a>
                  </li>
                  <li v-if="canReauthorize">
                    <a class="dropdown-item" :class="{ disabled: startAuthorizationPending }" href="#" @click.prevent="reconnect"><i class="bi bi-box-arrow-up-right me-2"></i>Re-authorize</a>
                  </li>
                  <li v-if="secretFields.length > 0">
                    <a class="dropdown-item" href="#" @click.prevent="openRotate"><i class="bi bi-arrow-repeat me-2"></i>Rotate secrets</a>
                  </li>
                  <li v-if="canRefresh || canReauthorize || secretFields.length > 0"><hr class="dropdown-divider" /></li>
                  <li><a class="dropdown-item text-danger" href="#" @click.prevent="remove"><i class="bi bi-trash me-2"></i>Delete</a></li>
                </ul>
              </div>
            </template>
          </PageHeader>

          <!-- Status + summary -->
          <div class="mb-4">
            <span class="badge" :class="connectionBadgeClass(connection)">{{ connectionBadgeLabel(connection) }}</span>
          </div>

          <!-- Setup never finished, or auth broke: prompt to reconnect (oauth re-authorizes, others re-enter creds) -->
          <div v-if="showReconnect" class="alert alert-warning d-flex justify-content-between align-items-center">
            <span>
              <i class="bi bi-exclamation-triangle me-2"></i>
              {{ connection.status === "PendingSetup"
                ? "This connection is awaiting authorization."
                : (connection.lastAuthFailure?.reason
                    ? `This connection needs re-authorization: ${connection.lastAuthFailure.reason}`
                    : "This connection needs re-authorization. Reconnect to restore it.") }}
            </span>
            <button class="bb-btn-primary-cta btn-sm" :disabled="reconnectBusy" @click="reconnect">
              <span v-if="reconnectBusy" class="spinner-border spinner-border-sm me-1" role="status"></span>
              {{ reconnectLabel }}
            </button>
          </div>

          <ul class="nav nav-tabs mb-4" role="tablist">
            <li v-for="tab in tabs" :key="tab.id" class="nav-item" role="presentation">
              <button
                class="nav-link"
                :class="{ active: activeTab === tab.id }"
                type="button"
                role="tab"
                @click="setActiveTab(tab.id)"
              >
                {{ tab.label }}
              </button>
            </li>
          </ul>

          <dl v-if="activeTab === 'overview'" class="row mb-0">
            <dt class="col-sm-3">Template</dt>
            <dd class="col-sm-9">{{ templateName }}</dd>

            <dt class="col-sm-3">Authentication</dt>
            <dd class="col-sm-9">{{ connectionMethod?.label ?? connectionMethod?.kind ?? connection.authMethodId }}</dd>

            <template v-for="(value, key) in connection.config" :key="key">
              <dt class="col-sm-3 text-capitalize">{{ key }}</dt>
              <dd class="col-sm-9">{{ value }}</dd>
            </template>

            <template v-if="Object.keys(connection.linkedCredentials).length > 0">
              <dt class="col-sm-3">Linked credentials</dt>
              <dd class="col-sm-9">
                <div v-for="(credId, role) in connection.linkedCredentials" :key="role">
                  <span class="text-capitalize">{{ role }}</span>: <code>{{ credId }}</code>
                </div>
              </dd>
            </template>

            <template v-if="connection.expiresAt">
              <dt class="col-sm-3">Token expires</dt>
              <dd class="col-sm-9">{{ fmt(connection.expiresAt) }}</dd>
            </template>

            <dt class="col-sm-3">Rate limit</dt>
            <dd class="col-sm-9">
              <template v-if="editingRate">
                <div class="input-group input-group-sm" style="max-width: 20rem">
                  <input v-model="rateSends" type="number" min="1" class="form-control" placeholder="sends" />
                  <span class="input-group-text">per</span>
                  <input v-model="ratePerSeconds" type="number" min="1" class="form-control" placeholder="secs" />
                  <button class="btn btn-primary" :disabled="updateConnectionPending" @click="saveRate">
                    Save
                  </button>
                  <button class="btn btn-outline-secondary" @click="editingRate = false">Cancel</button>
                </div>
                <div class="form-text">Clear both to go back to {{ declaredRate }}.</div>
              </template>
              <template v-else>
                {{ effectiveRate }}
                <span v-if="rateSource" class="text-muted"> · {{ rateSource }}</span>
                <button class="btn btn-link btn-sm p-0 ms-2" @click="startEditingRate">Change</button>
              </template>
            </dd>

            <dt class="col-sm-3">Last test</dt>
            <dd class="col-sm-9">
              <span v-if="connection.lastTestResult" :class="connection.lastTestResult.success ? 'text-success' : 'text-danger'">
                {{ connection.lastTestResult.success ? "Passed" : "Failed" }}
              </span>
              <span v-else class="text-muted">Never tested</span>
              <span class="text-muted"> · {{ fmt(connection.lastTestResult?.testedAt) }}</span>
            </dd>

            <dt class="col-sm-3">Created</dt>
            <dd class="col-sm-9">{{ fmt(connection.createdAt) }}</dd>
          </dl>

          <ConnectionDiagnosticsTab v-else-if="activeTab === 'diagnostics'" :connection-id="connection.id" />
        </template>
      </PanelCard>
    </PanelGrid>

    <!-- Rotate secrets modal -->
    <div
      v-if="showRotate"
      class="modal fade show d-block"
      tabindex="-1"
      @click.self="showRotate = false"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h2 class="modal-title">Rotate secrets</h2>
            <button type="button" class="btn-close" @click="showRotate = false"></button>
          </div>
          <div class="modal-body">
            <p class="text-muted small">Re-enter the credential material. Existing values are never shown.</p>
            <div v-for="f in secretFields" :key="f.name" class="mb-3">
              <label :for="`rot-${f.name}`" class="form-label text-capitalize">{{ f.name }}</label>
              <input :id="`rot-${f.name}`" v-model="rotateValues[f.name]" type="password" class="form-control" autocomplete="new-password" />
            </div>
          </div>
          <div class="modal-footer">
            <button class="bb-btn-cancel" @click="showRotate = false">Cancel</button>
            <button class="bb-btn-primary-cta" :disabled="rotateConnectionPending" @click="confirmRotate">
              <span v-if="rotateConnectionPending" class="spinner-border spinner-border-sm me-1" role="status"></span>
              Rotate
            </button>
          </div>
        </div>
      </div>
    </div>
    <div v-if="showRotate" class="modal-backdrop fade show" @click="showRotate = false"></div>
  </main>
</template>
